import { useState, useMemo } from 'react';
import { useAdmin } from '../context/AdminContext';

export function ReaderPanel() {
  const { adminApp, reader } = useAdmin();
  
  // Form states for different operations
  const [participantId, setParticipantId] = useState('');
  const [threadId, setThreadId] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [scrollAmount, setScrollAmount] = useState(10);
  const [windowSize, setWindowSize] = useState(128);
  const [cacheSize, setCacheSize] = useState(1024);
  const [windowLimit, setWindowLimit] = useState<number | undefined>(undefined);
  
  // View mode
  const [view, setView] = useState<'state' | 'window'>('state');

  const handleGetReaderState = async () => {
    if (!participantId.trim() || !threadId.trim()) {
      alert('Participant ID and Thread ID are required');
      return;
    }
    await reader.getReaderState(participantId, threadId);
    setView('state');
  };

  const handleScrollToEnd = async () => {
    if (!participantId.trim() || !threadId.trim() || !conversationId.trim()) {
      alert('Participant ID, Thread ID, and Conversation ID are required');
      return;
    }
    await reader.scrollToEnd({
      participantId,
      threadId,
      conversationId,
      workspaceId: adminApp.workspaceId,
      windowSize,
      cacheSize,
    });
    setView('state');
  };

  const handleScrollUp = async () => {
    if (!participantId.trim() || !threadId.trim()) {
      alert('Participant ID and Thread ID are required');
      return;
    }
    await reader.scrollUp({ participantId, threadId, amount: scrollAmount });
    setView('state');
  };

  const handleScrollDown = async () => {
    if (!participantId.trim() || !threadId.trim()) {
      alert('Participant ID and Thread ID are required');
      return;
    }
    await reader.scrollDown({ participantId, threadId, amount: scrollAmount });
    setView('state');
  };

  const handleGetWindow = async () => {
    if (!participantId.trim() || !threadId.trim()) {
      alert('Participant ID and Thread ID are required');
      return;
    }
    await reader.getWindow(participantId, threadId, windowLimit);
    setView('window');
  };

  // Statistics
  const stats = useMemo(() => {
    if (!reader.readerState) return null;
    return {
      cacheSize: reader.readerState.cacheEndPosition - reader.readerState.cacheStartPosition,
      cachedPercentage: reader.readerState.totalMessages > 0
        ? ((reader.readerState.cacheEndPosition - reader.readerState.cacheStartPosition) / reader.readerState.totalMessages * 100).toFixed(1)
        : '0.0',
      position: `${reader.readerState.currentPosition} / ${reader.readerState.totalMessages}`,
      positionPercentage: reader.readerState.totalMessages > 0
        ? ((reader.readerState.currentPosition / reader.readerState.totalMessages) * 100).toFixed(1)
        : '0.0',
    };
  }, [reader.readerState]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Reader</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage message scrolling, caching, and window state for participants
          </p>
        </div>
        <button
          onClick={() => reader.clearState()}
          className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          Clear State
        </button>
      </div>

      {/* Error Display */}
      {reader.error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{reader.error}</p>
        </div>
      )}

      {/* Input Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Reader Parameters</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Participant ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Participant ID
            </label>
            <input
              type="text"
              value={participantId}
              onChange={(e) => setParticipantId(e.target.value)}
              placeholder="Enter participant ID..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900"
            />
          </div>

          {/* Thread ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Thread ID (UUID)
            </label>
            <input
              type="text"
              value={threadId}
              onChange={(e) => setThreadId(e.target.value)}
              placeholder="Enter thread UUID..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900"
            />
          </div>

          {/* Conversation ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Conversation ID (UUID)
            </label>
            <input
              type="text"
              value={conversationId}
              onChange={(e) => setConversationId(e.target.value)}
              placeholder="Enter conversation UUID..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900"
            />
          </div>

          {/* Scroll Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Scroll Amount
            </label>
            <input
              type="number"
              value={scrollAmount}
              onChange={(e) => setScrollAmount(parseInt(e.target.value, 10))}
              min={1}
              max={256}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900"
            />
          </div>

          {/* Window Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Window Size
            </label>
            <input
              type="number"
              value={windowSize}
              onChange={(e) => setWindowSize(parseInt(e.target.value, 10))}
              min={1}
              max={256}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900"
            />
          </div>

          {/* Cache Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Cache Size
            </label>
            <input
              type="number"
              value={cacheSize}
              onChange={(e) => setCacheSize(parseInt(e.target.value, 10))}
              min={16}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900"
            />
          </div>

          {/* Window Limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Window Limit (optional)
            </label>
            <input
              type="number"
              value={windowLimit ?? ''}
              onChange={(e) => setWindowLimit(e.target.value ? parseInt(e.target.value, 10) : undefined)}
              placeholder="Leave empty for default"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleGetReaderState}
            disabled={reader.loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Get Reader State
          </button>
          <button
            onClick={handleScrollToEnd}
            disabled={reader.loading}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Scroll to End
          </button>
          <button
            onClick={handleScrollUp}
            disabled={reader.loading}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Scroll Up
          </button>
          <button
            onClick={handleScrollDown}
            disabled={reader.loading}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Scroll Down
          </button>
          <button
            onClick={handleGetWindow}
            disabled={reader.loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Get Window
          </button>
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Messages</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {reader.readerState?.totalMessages ?? 0}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Current Position</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {stats.position}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                {stats.positionPercentage}%
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Cache Size</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {stats.cacheSize}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                {stats.cachedPercentage}%
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Window Size</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {reader.readerState?.windowSize ?? 0}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reader State Display */}
      {view === 'state' && reader.readerState && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reader State</h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Participant ID:</span>
                  <p className="text-sm text-gray-900 dark:text-gray-100 font-mono mt-1">{reader.readerState.participantId}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Thread ID:</span>
                  <p className="text-sm text-gray-900 dark:text-gray-100 font-mono mt-1">{reader.readerState.threadId}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Conversation ID:</span>
                  <p className="text-sm text-gray-900 dark:text-gray-100 font-mono mt-1">{reader.readerState.conversationId}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Workspace ID:</span>
                  <p className="text-sm text-gray-900 dark:text-gray-100 font-mono mt-1">{reader.readerState.workspaceId}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cache Boundaries</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">Start:</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 font-mono">{reader.readerState.cacheStartPosition}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">End:</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 font-mono">{reader.readerState.cacheEndPosition}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">Total:</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 font-mono">{reader.readerState.totalMessages}</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Window</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">Position:</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 font-mono">{reader.readerState.currentPosition}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">Window Size:</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 font-mono">{reader.readerState.windowSize}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">Cache Size:</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 font-mono">{reader.readerState.cacheSize}</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <span className="text-xs text-gray-600 dark:text-gray-400">Updated At:</span>
                <p className="text-sm text-gray-900 dark:text-gray-100 font-mono mt-1">
                  {new Date(reader.readerState.updatedAt).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reader Window Display */}
      {view === 'window' && reader.readerWindow && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reader Window</h3>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {reader.readerWindow.messages.length} messages ({reader.readerWindow.windowStart} - {reader.readerWindow.windowEnd} of {reader.readerWindow.totalMessages})
            </span>
          </div>
          <div className="p-6">
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {reader.readerWindow.messages.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No messages in window</p>
              ) : (
                reader.readerWindow.messages.map((message, idx) => (
                  <div
                    key={message.id || idx}
                    className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
                          {message.participantId}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-500">
                          {message.role}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-500">
                        {new Date(message.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {message.content}
                    </p>
                    {message.id && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 font-mono mt-2">
                        ID: {message.id}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
            {reader.readerWindow.hasMore && (
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-4">
                More messages available...
              </p>
            )}
          </div>
        </div>
      )}

      {/* Loading State */}
      {reader.loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
}

